import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "react-feather";
import { SmileAdd } from "@webng/icons-common";
import { resolveReactionOptions } from "@webng/liveblog-reactions";
import { useAuthenticatedApi } from "@webng/react-app-common";
import { ApiClient, ApiResponseError } from "@webng/tickaroo-api";
import { useReactionsDataForEvent } from "../reactions/useReactionsData";
import { useModalAccessibility } from "../utils/useModalAccessibility";
import { useTrackingFunction } from "../LiveblogRenderContext";

type ReactionButtonProps = {
  readonly reaction: {
    readonly id: string;
    readonly emoji: string;
    readonly label: string;
    readonly count: number;
    readonly isUserReaction: boolean;
  };
  readonly onReactionClick: (reactionId: string, isActive: boolean) => void;
  readonly numberFormat: Intl.NumberFormat;
}

function ReactionButton({ reaction, onReactionClick, numberFormat }: ReactionButtonProps) {
  const [animationState, setAnimationState] = useState<'initial' | 'go-up' | 'wait-down'>('initial');
  const [displayCount, setDisplayCount] = useState(reaction.count);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const [shouldAnimateActive, setShouldAnimateActive] = useState(false);

  useEffect(() => {
    setHasInitiallyRendered(true);
  }, []);


  useEffect(() => {
    if (hasInitiallyRendered && reaction.isUserReaction && !shouldAnimateActive) {
      setShouldAnimateActive(true);
    }
  }, [reaction.isUserReaction, hasInitiallyRendered, shouldAnimateActive]);


  useEffect(() => {
    if (reaction.count !== displayCount) {

      setAnimationState('go-up');


      setTimeout(() => {
        setDisplayCount(reaction.count);
      }, 100);


      setTimeout(() => {
        setAnimationState('wait-down');
      }, 100);


      setTimeout(() => {
        setAnimationState('initial');
      }, 200);
    }
  }, [reaction.count, displayCount]);

  const handleClick = () => {
    if (animationState === 'initial') {
      onReactionClick(reaction.id, reaction.isUserReaction);
    }
  };

  const isAnimating = animationState !== 'initial';

  return (
    <button
      key={reaction.id}
      title={reaction.label}
      type="button"
      onClick={handleClick}
      disabled={isAnimating}
      className={`tik4-r__rb tik4-r__rb--reaction ${reaction.isUserReaction ? 'tik4-r__rb--active' : ''} ${shouldAnimateActive && reaction.isUserReaction ? 'tik4-r__rb--active-animated' : ''} ${isAnimating ? 'tik4-r__rb--animating' : ''} ${displayCount === 0 ? 'tik4-r__rb--zero' : ''}`}
      aria-pressed={reaction.isUserReaction}
    >
      <div className="tik4-r__rb__c">
        <div className="tik4-r__rb__c__i">
          {reaction.emoji}
        </div>
        <div className={`tik4-r__rb__c__t tik4-r__rb__c__t--${animationState}`}>
          {numberFormat.format(displayCount)}
        </div>
      </div>
    </button>
  );
}

type EventReactionsProps = {
  readonly reactionSetId: string;
  readonly organizationId: string;
  readonly liveblogId: string;
  readonly eventId: string;
  readonly reactionCounts?: {
    [reactionId: string]: number;
  };
  readonly eventRef?: React.RefObject<HTMLDivElement>;
}

export default function EventReactions({ reactionSetId, organizationId, liveblogId, eventId, reactionCounts, eventRef }: EventReactionsProps) {
  const {t, i18n} = useTranslation("liveblog");
  const numberFormat = new Intl.NumberFormat(i18n.language, {
    notation: "compact",
    compactDisplay: "short"
  });
  const api = useAuthenticatedApi();
  const ariaId = `tik4-reactions-${eventId}`
  const [localReactionCounts, setLocalReactionCounts] = useState<{ [reactionId: string]: number }>({});
  const reactionsData = useReactionsDataForEvent(liveblogId, eventId);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const [isLoading, setIsLoading] = useState<{ [reactionId: string]: boolean }>({});
  const trackingFunction = useTrackingFunction();
  const { userId, userReactions, addUserReaction, removeUserReaction } = reactionsData /* for server-side rendering */ || {
    userId: undefined,
    userReactions: {} as { [reactionId: string]: boolean },
    addUserReaction: () => {},
    removeUserReaction: () => {}
  };

  useEffect(() => {
    if (reactionCounts) {
      setLocalReactionCounts(reactionCounts);
    } else {
      // empty object would be truthy - God knows why
      setLocalReactionCounts(Object.fromEntries(Object.entries(userReactions).map(([reactionId, isUserReaction]) => [reactionId, isUserReaction ? 1 : 0])));
    }
  }, [reactionCounts, userReactions]);

  const reactions = useMemo(() => resolveReactionOptions(reactionSetId, t), [reactionSetId, t]);

  const activeReactions = useMemo(() => {
    if (!localReactionCounts) return [];
    return reactions
      .filter(reaction => localReactionCounts[reaction.id] > 0)
      .map(reaction => ({
        ...reaction,
        count: localReactionCounts[reaction.id],
        isUserReaction: userReactions[reaction.id]
      }));
  }, [reactions, localReactionCounts, userReactions]);

  const addReaction = useCallback(async (reactionId: string) => {
    if (isLoading[reactionId]) return;

    setIsLoading(prev => ({ ...prev, [reactionId]: true }));

    trackingFunction({t: 'a_rct', e: eventId, id: reactionId});

    try {

      setLocalReactionCounts(prev => ({
        ...prev,
        [reactionId]: (prev[reactionId] || 0) + 1
      }));
      addUserReaction(reactionId);

      await api.postJson(ApiClient.API_INTERACTIVE_V1_REACTIONS_CREATE, {
        reactionId,
        userId
      }, {
        organizationId: organizationId,
        liveblogId,
        eventId
      });
    } catch (err) {

      setLocalReactionCounts(prev => ({
        ...prev,
        [reactionId]: Math.max(0, (prev[reactionId] || 0) - 1)
      }));

      if (err instanceof ApiResponseError && err.status === 409) {

        return;
      }

      removeUserReaction(reactionId);
      console.error(err);
    } finally {
      setIsLoading(prev => ({ ...prev, [reactionId]: false }));
    }
  }, [api, organizationId, liveblogId, eventId, userId, addUserReaction, removeUserReaction, isLoading, trackingFunction]);

  const removeReaction = useCallback(async (reactionId: string) => {
    if (isLoading[reactionId]) return;

    setIsLoading(prev => ({ ...prev, [reactionId]: true }));

    try {

      setLocalReactionCounts(prev => ({
        ...prev,
        [reactionId]: Math.max(0, (prev[reactionId] || 0) - 1)
      }));
      removeUserReaction(reactionId);

      await api.deleteJson(ApiClient.API_INTERACTIVE_V1_REACTIONS_DELETE, {
        reactionId,
        userId
      }, {
        organizationId: organizationId,
        liveblogId,
        eventId
      });
    } catch (err) {

      setLocalReactionCounts(prev => ({
        ...prev,
        [reactionId]: (prev[reactionId] || 0) + 1
      }));

      if (err instanceof ApiResponseError && err.status === 404) {

        return;
      }

      addUserReaction(reactionId);
    } finally {
      setIsLoading(prev => ({ ...prev, [reactionId]: false }));
    }
  }, [api, organizationId, liveblogId, eventId, userId, addUserReaction, removeUserReaction, isLoading]);

  const [isOpen, setIsOpen] = useState(false);

  const { handleClose } = useModalAccessibility({
    isOpen,
    onClose: () => setIsOpen(false),
    dialogRef,
    triggerRef: triggerButtonRef,
    focusSelector: '.tik4-r__i__button, .tik4-r__i__close__button'
  });

  const onReactionClick = useCallback(async (reactionId: string, isActive: boolean) => {
    isOpen && !isLoading[reactionId] && handleClose();
    try {
      if (isActive) {
        await removeReaction(reactionId);
      } else {
        await addReaction(reactionId);
      }
    } catch (error) {
      console.error('Failed to update reaction:', error);
    }
  }, [addReaction, removeReaction, handleClose, isOpen, isLoading]);

  const handleTriggerClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const event = eventRef?.current;
    if (!isOpen && dialog) {
      // reset translate and max width
      dialog.style.translate = "";
      dialog.style.maxWidth = "";
      return;
    }
    if (!event || !dialog) return;
    requestAnimationFrame(() => {
      const margin = 5;

      if (dialog && event) {
        const eventRect = event.getBoundingClientRect();
        // set max width
        dialog.style.maxWidth = eventRect.width - margin * 2 + 'px';

        const dialogRect = dialog.getBoundingClientRect();

        const leftOffset = dialogRect.left - eventRect.left;
        const rightOffset = eventRect.right - dialogRect.right;

        if (rightOffset < 0) {
          dialog.style.translate = `${rightOffset - margin}px 0`;
        } else if (leftOffset < 0) {
          dialog.style.translate = `${leftOffset * -1 + margin}px 0`;
        }
      }
    });
  }, [isOpen, eventRef]);

  // Unknown or malformed reaction_set_id resolves to no reactions — render nothing (no crash).
  if (reactions.length === 0) {
    return null;
  }

  return <div className={`tik4-r ${reactions.length > 1 ? 'tik4-r--multiple' : 'tik4-r--single'} ${isOpen ? 'tik4-r--open' : 'tik4-r--close'} ${activeReactions.length > 0 ? `tik4-r--active-reactions tik4-r--active-reactions--${activeReactions.length}` : 'tik4-r--no-reactions'}`}>
      <div className="tik4-r__c">

          { reactions.length > 1 ?
            <>

              <div
                ref={dialogRef}
                className={`tik4-r__i ${isOpen ? 'tik4-r__i--open' : 'tik4-r__i--close'}`}
                role="dialog"
                aria-modal="true"
                aria-label={t("liveblog.reactions.modal.label")}
                aria-hidden={!isOpen}
                id={ariaId}
              >
                <div className="tik4-r__i__bl">
                  { reactions.map(reaction => {
                      const isActive = userReactions[reaction.id];
                      return <button
                          type="button"
                          title={reaction.label}
                          key={reaction.id}
                          onClick={() => onReactionClick(reaction.id, isActive)}
                          disabled={false}
                          className={`tik4-r__i__b ${isActive ? 'tik4-r__i__b--active' : ''}`}
                          aria-pressed={isActive}
                      >
                        <span className="tik4-r__i__b__c">
                          {reaction.emoji}
                        </span>
                      </button>
                    })
                  }
                </div>
                <div className="tik4-r__i__cl">
                  <button
                    className="tik4-r__i__cl__b"
                    title={t("liveblog.action.close")}
                    onClick={handleClose}
                    aria-controls={ariaId}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    type="button"
                  >
                    <X className="tik4-icon tik4-r__i__cl__icon" />
                  </button>
                </div>
              </div>
              <div className="tik4-r__rl">
                {activeReactions.length > 0 && activeReactions.map(reaction => (
                    <ReactionButton
                        key={reaction.id}
                        reaction={reaction}
                        onReactionClick={onReactionClick}
                        numberFormat={numberFormat}
                    />
                ))}

                <button
                  ref={triggerButtonRef}
                  type="button"
                  className="tik4-r__rb tik4-r__rb--trigger"
                  onClick={handleTriggerClick}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  aria-controls={ariaId}
                  aria-label={"React"}
                  title={t("liveblog.reactions.modal.trigger")}
                >
                  <div className="tik4-r__rb__c">
                    <div className="tik4-r__rb__c__i">
                      <SmileAdd className="tik4-icon tik4-r__rb__icon" />
                    </div>
                    <div className="tik4-r__rb__c__t">
                      {t("liveblog.reactions.modal.trigger")}
                    </div>
                  </div>
                </button>

              </div>
            </>
            :
            <div className="tik4-r__sr">
              <ReactionButton
                key={reactions[0].id}
                reaction={{
                  id: reactions[0].id,
                  emoji: reactions[0].emoji,
                  label: reactions[0].label,
                  count: localReactionCounts[reactions[0].id] || 0,
                  isUserReaction: userReactions[reactions[0].id]
                }}
                onReactionClick={onReactionClick}
                numberFormat={numberFormat}
              />
            </div>
          }
      </div>
  </div>
}
